import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Calendar } from 'primereact/calendar';
import { InputNumber } from 'primereact/inputnumber';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';

import apiFetch from '../../auth/apiFetch';
import { properties } from '../../properties';

const BASE = `${properties.baseURL}/admin/annual-fees`;

const parseDate = (str) => {
    if (!str) return null;
    const [y, m, d] = str.split('-').map(Number);
    return new Date(y, m - 1, d);
};

const formatDate = (date) => {
    if (!date) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const emptyPrice = () => ({ validUntil: '', value: null });

const extractError = async (res) => {
    const body = await res.json().catch(() => ({}));
    return body.error || body.message || `Erro ${res.status}`;
};

export default function AnnualFeeList() {
    const [fees, setFees] = useState([]);
    const [dialog, setDialog] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [editingYear, setEditingYear] = useState(null);
    const [year, setYear] = useState(null);
    const [prices, setPrices] = useState([emptyPrice()]);
    const [submitted, setSubmitted] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const toast = useRef(null);

    useEffect(() => { load(); }, []);

    const load = async () => {
        try {
            const res = await apiFetch(BASE);
            if (!res.ok) throw new Error(`Erro ${res.status}`);
            const data = await res.json();
            data.sort((a, b) => b.year - a.year);
            setFees(data);
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: 'Erro', detail: err.message, life: 4000 });
        }
    };

    const openNew = () => {
        setEditingYear(null);
        setYear(null);
        setPrices([emptyPrice()]);
        setSubmitted(false);
        setDialog(true);
    };

    const openEdit = (f) => {
        setEditingYear(f.year);
        setYear(f.year);
        setPrices(f.prices.map(p => ({ ...p })));
        setSubmitted(false);
        setDialog(true);
    };

    const confirmDelete = (f) => {
        setDeleteTarget(f);
        setDeleteDialog(true);
    };

    const isPriceValid = (p) => p.validUntil && p.value != null && p.value >= 1;

    const lastBatchOk = () => {
        const effectiveYear = editingYear ?? year;
        if (!effectiveYear || prices.length === 0) return true;
        const last = prices[prices.length - 1];
        return last.validUntil === `${effectiveYear}-12-31`;
    };

    const save = async () => {
        setSubmitted(true);
        const effectiveYear = editingYear ?? year;
        if (!effectiveYear || effectiveYear < 2000) return;
        if (prices.length === 0 || prices.some(p => !isPriceValid(p))) return;
        if (!lastBatchOk()) return;

        const body = { year: effectiveYear, prices };
        try {
            let saved;
            if (editingYear != null) {
                const res = await apiFetch(`${BASE}/${editingYear}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                });
                if (!res.ok) throw new Error(await extractError(res));
                saved = await res.json();
                setFees(prev =>
                    prev.map(f => f.year === editingYear ? saved : f)
                        .sort((a, b) => b.year - a.year)
                );
            } else {
                const res = await apiFetch(BASE, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                });
                if (!res.ok) throw new Error(await extractError(res));
                saved = await res.json();
                setFees(prev => [...prev, saved].sort((a, b) => b.year - a.year));
            }
            setDialog(false);
            toast.current.show({ severity: 'success', summary: 'Sucesso', detail: 'Anuidade salva', life: 3000 });
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Erro', detail: err.message, life: 5000 });
        }
    };

    const doDelete = async () => {
        try {
            const res = await apiFetch(`${BASE}/${deleteTarget.year}`, { method: 'DELETE' });
            if (res.status === 409) {
                const body = await res.json().catch(() => ({}));
                toast.current.show({
                    severity: 'warn', summary: 'Não foi possível excluir',
                    detail: body.error || body.message || 'Existem inscrições usando esta anuidade',
                    life: 6000,
                });
                setDeleteDialog(false);
                return;
            }
            if (!res.ok && res.status !== 204) throw new Error(await extractError(res));
            setFees(prev => prev.filter(f => f.year !== deleteTarget.year));
            toast.current.show({ severity: 'success', summary: 'Sucesso', detail: 'Anuidade excluída', life: 3000 });
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Erro', detail: err.message, life: 5000 });
        } finally {
            setDeleteDialog(false);
            setDeleteTarget(null);
        }
    };

    const updatePrice = (idx, field, value) => {
        setPrices(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
    };

    const addPrice = () => setPrices(prev => [...prev, emptyPrice()]);
    const removePrice = (idx) => setPrices(prev => prev.filter((_, i) => i !== idx));

    const pricesSummary = (row) => {
        if (!row.prices || row.prices.length === 0) return '-';
        if (row.prices.length === 1) return `R$ ${row.prices[0].value}`;
        return `${row.prices.length} lotes · R$ ${row.prices[0].value} → R$ ${row.prices[row.prices.length - 1].value}`;
    };

    const effectiveYear = editingYear ?? year;
    const showLastBatchWarning = submitted && !lastBatchOk();

    const dialogFooter = (
        <>
            <Button label="Cancelar" icon="pi pi-times" className="p-button-text" onClick={() => setDialog(false)} />
            <Button label="Salvar" icon="pi pi-check" onClick={save} />
        </>
    );

    const deleteFooter = (
        <>
            <Button label="Não" icon="pi pi-times" className="p-button-text" onClick={() => setDeleteDialog(false)} />
            <Button label="Sim" icon="pi pi-check" className="p-button-text" onClick={doDelete} />
        </>
    );

    return (
        <div className="surface-card p-4 border-round shadow-2 mb-4">
            <Toast ref={toast} />
            <div className="flex justify-content-between align-items-center mb-3">
                <span className="text-2xl text-800 font-bold">Anuidade</span>
                <Button label="Novo Ano" icon="pi pi-plus" className="p-button-sm" onClick={openNew} />
            </div>

            <DataTable value={fees} emptyMessage="Nenhuma anuidade cadastrada">
                <Column field="year" header="Ano" style={{ width: '6rem' }} />
                <Column header="Lotes" body={pricesSummary} />
                <Column body={(row) => (
                    <>
                        <Button icon="pi pi-pencil"
                            className="p-button-rounded p-button-success mr-2 p-button-sm"
                            onClick={() => openEdit(row)} />
                        <Button icon="pi pi-trash"
                            className="p-button-rounded p-button-danger p-button-sm"
                            onClick={() => confirmDelete(row)} />
                    </>
                )} style={{ width: '9rem' }} />
            </DataTable>

            <Dialog visible={dialog} style={{ width: '580px' }}
                header={editingYear != null ? `Editar Anuidade ${editingYear}` : 'Nova Anuidade'}
                modal className="p-fluid" footer={dialogFooter}
                onHide={() => setDialog(false)}>
                <div className="formgrid grid">
                    {editingYear == null ? (
                        <div className="field col-12 md:col-4">
                            <label>Ano *</label>
                            <InputNumber value={year}
                                onValueChange={(e) => setYear(e.value)}
                                mode="decimal" minFractionDigits={0} maxFractionDigits={0}
                                useGrouping={false} min={2000} max={2100} placeholder="2026"
                                className={submitted && (!year || year < 2000) ? 'p-invalid' : ''} />
                            {submitted && (!year || year < 2000) && (
                                <small className="p-error">Obrigatório</small>
                            )}
                        </div>
                    ) : (
                        <div className="field col-12 md:col-4">
                            <label>Ano</label>
                            <div className="p-2 text-xl font-bold">{editingYear}</div>
                        </div>
                    )}

                    <div className="field col-12">
                        <div className="flex align-items-center justify-content-between mb-2">
                            <label className="mb-0">Lotes *</label>
                            <Button label="Adicionar lote" icon="pi pi-plus"
                                className="p-button-sm p-button-outlined"
                                onClick={addPrice} type="button" />
                        </div>

                        {showLastBatchWarning && (
                            <Message severity="warn" className="w-full mb-2"
                                style={{ justifyContent: 'flex-start' }}
                                text={`O último lote deve terminar em 31/12/${effectiveYear}. Sem isso a anuidade fica sem preço em dezembro e os exames somem do portal.`} />
                        )}

                        <DataTable value={prices} emptyMessage="Adicione ao menos um lote">
                            <Column header="Vale até" body={(p, { rowIndex: idx }) => (
                                <Calendar value={parseDate(p.validUntil)}
                                    onChange={(e) => updatePrice(idx, 'validUntil', formatDate(e.value))}
                                    dateFormat="dd/mm/yy" showIcon
                                    className={submitted && !p.validUntil ? 'p-invalid' : ''} />
                            )} />
                            <Column header="Valor (R$)" body={(p, { rowIndex: idx }) => (
                                <InputNumber value={p.value}
                                    onValueChange={(e) => updatePrice(idx, 'value', e.value)}
                                    mode="decimal" minFractionDigits={0} maxFractionDigits={0}
                                    useGrouping={false} min={1}
                                    className={submitted && (p.value == null || p.value < 1) ? 'p-invalid' : ''} />
                            )} />
                            <Column style={{ width: '4rem' }} body={(p, { rowIndex: idx }) => (
                                prices.length > 1 && (
                                    <Button icon="pi pi-trash"
                                        className="p-button-rounded p-button-danger p-button-sm"
                                        onClick={() => removePrice(idx)} type="button" />
                                )
                            )} />
                        </DataTable>

                        {submitted && prices.some(p => !isPriceValid(p)) && (
                            <small className="p-error block mt-1">
                                Preencha data e valor em todos os lotes
                            </small>
                        )}
                    </div>
                </div>
            </Dialog>

            <Dialog visible={deleteDialog} style={{ width: '450px' }}
                header="Confirmar Exclusão" modal footer={deleteFooter}
                onHide={() => setDeleteDialog(false)}>
                <div className="flex align-items-center">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                    {deleteTarget && (
                        <span>Deseja excluir a anuidade de <b>{deleteTarget.year}</b>?</span>
                    )}
                </div>
            </Dialog>
        </div>
    );
}
